import { useEffect, useMemo } from "react";
import { getReport } from "../../../services/reportsApi";
import {
  dispatchReportReady,
  subscribeReportReady,
} from "../../../utils/reportReadyEvents";
import {
  isReportStillProcessingStatus,
  scheduleReportProcessingFallbackPoll,
} from "../../../utils/reportProcessingFallbackPoll";
import type { WorkspaceDetail } from "../../../types/workspace";
import {
  isOptimisticWorkspaceReportNodeId,
  isPendingOptimisticReportEntityId,
} from "./workspaceOptimisticReport";

function workspaceHasProcessingReport(detail: WorkspaceDetail | null): boolean {
  if (!detail) return false;
  return detail.nodes.some(
    (node) =>
      node.type === "report" &&
      !(
        isOptimisticWorkspaceReportNodeId(node.id) &&
        isPendingOptimisticReportEntityId(node.entityId)
      ) &&
      (node.meta?.status === "processing" || node.meta?.status === "draft")
  );
}

function workspaceHasReportEntity(detail: WorkspaceDetail | null, reportId: string): boolean {
  if (!detail) return false;
  return detail.nodes.some((node) => node.type === "report" && node.entityId === reportId);
}

function processingReportEntityIds(detail: WorkspaceDetail | null): string[] {
  if (!detail) return [];
  const ids: string[] = [];
  for (const node of detail.nodes) {
    if (node.type !== "report") continue;
    if (
      isOptimisticWorkspaceReportNodeId(node.id) &&
      isPendingOptimisticReportEntityId(node.entityId)
    ) {
      continue;
    }
    if (
      node.meta?.status === "processing" ||
      node.meta?.status === "draft"
    ) {
      const id = String(node.entityId ?? "").trim();
      if (id) ids.push(id);
    }
  }
  return ids;
}

export type UseWorkspaceReportRefreshOptions = {
  /** Fired when a linked report leaves processing (ready or failed event). */
  onReportReady?: (reportId: string, status: string) => void;
};

/** Refresh workspace graph when a linked report finishes generating. */
export function useWorkspaceReportRefresh(
  detail: WorkspaceDetail | null,
  refreshDetail: () => Promise<WorkspaceDetail | null>,
  options?: UseWorkspaceReportRefreshOptions
): void {
  const onReportReady = options?.onReportReady;
  const processingIdsKey = useMemo(
    () => processingReportEntityIds(detail).join("|"),
    [detail]
  );

  useEffect(() => {
    if (!workspaceHasProcessingReport(detail)) return;

    return subscribeReportReady((event) => {
      const reportId = event.reportId?.trim();
      if (!reportId || !workspaceHasReportEntity(detail, reportId)) return;
      const status = String(event.status ?? "");
      // Failed gens are dismissed + deleted by the host — skip a failed-status refresh flash.
      if (status.toLowerCase() === "failed") {
        onReportReady?.(reportId, status);
        return;
      }
      void (async () => {
        await refreshDetail();
        onReportReady?.(reportId, status);
      })();
    });
  }, [detail, onReportReady, refreshDetail]);

  // Soft safety when the tab is focused again (missed events while backgrounded).
  useEffect(() => {
    if (!workspaceHasProcessingReport(detail)) return;

    const onVisible = () => {
      if (document.visibilityState === "visible") void refreshDetail();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [detail, refreshDetail]);

  // Bounded fallback if WS report_ready was missed: GET /reports/{id} only,
  // once per minute up to 5 minutes; dispatch report_ready so canvas + drawer share one path.
  useEffect(() => {
    const ids = processingIdsKey ? processingIdsKey.split("|").filter(Boolean) : [];
    if (ids.length === 0) return;

    const cancels = ids.map((reportId) =>
      scheduleReportProcessingFallbackPoll(async () => {
        try {
          const report = await getReport(reportId);
          const status = String(report?.status ?? "");
          if (isReportStillProcessingStatus(status)) return;
          dispatchReportReady({ reportId, status: status || "published" });
        } catch {
          // Keep waiting — next tick or WS may still recover.
        }
      })
    );

    return () => {
      cancels.forEach((cancel) => cancel());
    };
  }, [processingIdsKey]);
}
